import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { fetchMe } from '../lib/auth';

export const Route = createFileRoute('/settings')({ component: SettingsPage });

interface Category { id: string; name: string; color: string }

const AVATAR_OPTIONS = ['🧙','🧝','🧛','🧜','🥷','👾','🤖','🐉','🦊','🐱','🐺','🦁','🦅','🐙','🦖','🪐'];

function SettingsPage() {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const cats = useQuery({ queryKey: ['categories'], queryFn: () => api<Category[]>('/api/categories') });

  return (
    <div className="mx-auto max-w-3xl space-y-8 px-6 py-6">
      <h1 className="text-2xl font-bold text-neutral-100">Profil & Ayarlar</h1>

      {me.data && <ProfileForm
        current={{ displayName: me.data.displayName, avatarEmoji: me.data.avatarEmoji }}
        onSaved={() => qc.invalidateQueries({ queryKey: ['me'] })}
      />}

      <PasswordForm />

      <CategoriesSection cats={cats.data ?? []}
                         onChange={() => qc.invalidateQueries({ queryKey: ['categories'] })} />
    </div>
  );
}

function ProfileForm({ current, onSaved }: {
  current: { displayName: string; avatarEmoji: string };
  onSaved: () => void;
}) {
  const [displayName, setDisplayName] = useState(current.displayName);
  const [avatarEmoji, setAvatarEmoji] = useState(current.avatarEmoji);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    setDisplayName(current.displayName);
    setAvatarEmoji(current.avatarEmoji);
  }, [current.displayName, current.avatarEmoji]);

  const save = useMutation({
    mutationFn: () => api('/api/me', {
      method: 'PATCH',
      body: JSON.stringify({ displayName, avatarEmoji }),
    }),
    onSuccess: () => { setMsg('Kaydedildi'); onSaved(); },
    onError: () => setMsg('Hata'),
  });

  return (
    <section className="rounded-xl border border-border-soft bg-bg-raised p-5">
      <h2 className="mb-3 text-sm font-semibold text-neutral-200">Profil</h2>
      <form onSubmit={(e) => { e.preventDefault(); setMsg(null); save.mutate(); }}
            className="space-y-4">
        <div>
          <label className="text-xs text-neutral-400">İsim</label>
          <input value={displayName} required minLength={1} maxLength={40}
                 onChange={(e) => setDisplayName(e.target.value)}
                 className="mt-1 w-full rounded-md bg-bg-elevated px-3 py-2 text-neutral-100" />
        </div>
        <div>
          <label className="text-xs text-neutral-400">Avatar</label>
          <div className="mt-2 grid grid-cols-8 gap-2">
            {AVATAR_OPTIONS.map((e) => (
              <button key={e} type="button"
                      onClick={() => setAvatarEmoji(e)}
                      className={`flex aspect-square items-center justify-center rounded-md border text-2xl transition ${
                        avatarEmoji === e
                          ? 'border-brand-400 bg-bg-elevated'
                          : 'border-border-soft/60 hover:border-brand-500/60'
                      }`}>
                {e}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button disabled={save.isPending}
                  className="rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50">
            Kaydet
          </button>
          {msg && <span className="text-xs text-neutral-400">{msg}</span>}
        </div>
      </form>
    </section>
  );
}

function PasswordForm() {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => api('/api/me/password', {
      method: 'POST',
      body: JSON.stringify({
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      }),
    }),
    onSuccess: () => {
      setMsg('Şifre güncellendi');
      setForm({ currentPassword: '', newPassword: '', confirm: '' });
    },
    onError: (e) => setErr(e instanceof Error && e.message.includes('401') ? 'Mevcut şifre yanlış' : 'Hata'),
  });

  return (
    <section className="rounded-xl border border-border-soft bg-bg-raised p-5">
      <h2 className="mb-3 text-sm font-semibold text-neutral-200">Şifre</h2>
      <form onSubmit={(e) => {
        e.preventDefault();
        setMsg(null); setErr(null);
        if (form.newPassword.length < 8) return setErr('Yeni şifre en az 8 karakter olmalı');
        if (form.newPassword !== form.confirm) return setErr('Yeni şifreler eşleşmiyor');
        save.mutate();
      }} className="space-y-3">
        <input type="password" placeholder="Mevcut şifre" value={form.currentPassword} required
               onChange={(e) => setForm({ ...form, currentPassword: e.target.value })}
               className="w-full rounded-md bg-bg-elevated px-3 py-2 text-neutral-100" />
        <input type="password" placeholder="Yeni şifre (min 8)" value={form.newPassword} required
               onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
               className="w-full rounded-md bg-bg-elevated px-3 py-2 text-neutral-100" />
        <input type="password" placeholder="Yeni şifre (tekrar)" value={form.confirm} required
               onChange={(e) => setForm({ ...form, confirm: e.target.value })}
               className="w-full rounded-md bg-bg-elevated px-3 py-2 text-neutral-100" />
        {err && <p className="text-sm text-red-400">{err}</p>}
        {msg && <p className="text-sm text-habit-400">{msg}</p>}
        <button disabled={save.isPending}
                className="rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50">
          Şifreyi değiştir
        </button>
      </form>
    </section>
  );
}

function CategoriesSection({ cats, onChange }: { cats: Category[]; onChange: () => void }) {
  const [newCat, setNewCat] = useState({ name: '', color: '#8b5cf6' });

  const add = useMutation({
    mutationFn: () => api('/api/categories', { method: 'POST', body: JSON.stringify(newCat) }),
    onSuccess: () => {
      onChange();
      setNewCat({ name: '', color: '#8b5cf6' });
    },
  });

  return (
    <section className="rounded-xl border border-border-soft bg-bg-raised p-5">
      <h2 className="mb-3 text-sm font-semibold text-neutral-200">Kategoriler</h2>
      <form onSubmit={(e) => { e.preventDefault(); add.mutate(); }}
            className="mb-3 flex gap-2">
        <input placeholder="Yeni kategori" value={newCat.name} required
               onChange={(e) => setNewCat({ ...newCat, name: e.target.value })}
               className="flex-1 rounded-md bg-bg-elevated px-3 py-2 text-neutral-100" />
        <input type="color" value={newCat.color}
               onChange={(e) => setNewCat({ ...newCat, color: e.target.value })}
               className="h-10 w-12 rounded" />
        <button className="rounded-md bg-brand-500 px-4 text-sm font-medium text-white hover:bg-brand-600">
          Ekle
        </button>
      </form>
      <ul className="space-y-1">
        {cats.map((c) => (
          <li key={c.id} className="flex items-center gap-2 rounded-md border border-border-soft/60 bg-bg-elevated/40 px-3 py-2">
            <span className="h-3 w-3 rounded-full" style={{ background: c.color }} />
            <span className="text-sm text-neutral-200">{c.name}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
